<template>
  <div class="c-app user-app role-theme-app" :class="{ 'c-dark-theme': $store.state.darkMode }">
    <TheHeader />
    <TheSidebar />

    <div
      class="c-wrapper"
      :class="{ 
        'sidebar-open': $store.state.sidebarShow && !$store.state.sidebarMinimize,
        'sidebar-minimized': $store.state.sidebarMinimize
      }"
    >
      <div class="c-body">
        <main class="c-main">
          <CContainer fluid>
            <router-view 
              ref="currentView"
              :active-tab-prop="activeTab"
              @tab-changed="onTabChanged"
            ></router-view>
          </CContainer>
        </main>
        <TheFooter />
      </div>
    </div>

    <!-- Mobile Bottom Navigation Bar -->
    <RoleBottomNav role="user" />

    <!-- Global Floating Report Action Button -->
    <FloatingReportFab />

    <CenterLoading />
  </div>
</template>

<script>
import TheSidebar from './Usidebar'
import TheHeader from './Uheader'
import RoleBottomNav from '@/components/common/RoleBottomNav.vue'
import FloatingReportFab from '@/components/common/FloatingReportFab.vue'
import TheFooter from '../TheFooter'
import { mapActions } from "vuex";

export default {
  name: 'TheContainer',
  components: {
    TheSidebar,
    TheHeader,
    RoleBottomNav,
    FloatingReportFab,
    TheFooter,
  },
  data() {
    return {
      activeTab: 'report'
    };
  },
  mounted() {
    document.body.classList.add('user-unified-rounded-ui');
    document.body.classList.add('role-unified-theme');
    this.loadUserProfile();
  },
  beforeDestroy() {
    document.body.classList.remove('user-unified-rounded-ui');
    document.body.classList.remove('role-unified-theme');
  },
  methods: {
    ...mapActions("user", ["getProfile"]),

    async loadUserProfile() {
      try {
        await this.getProfile();
      } catch (err) {
        console.error("Load profile error:", err);
      }
    },
    handleChangeTab(tabName) {
      this.activeTab = tabName;
      if (this.$refs.currentView && typeof this.$refs.currentView.setActiveTab === 'function') {
        this.$refs.currentView.setActiveTab(tabName);
      }
    },
    handleOpenReport() {
      if (this.$route.path !== '/user/new-request') {
        this.$router.push('/user/new-request');
      }
    },
    onTabChanged(tabName) {
      this.activeTab = tabName;
    }
  },
};
</script>

<style scoped>
.c-wrapper {
  transition: margin-left 0.3s ease;
  margin-left: 0;
}

.c-body {
  padding-top: 82px;
  min-height: 100vh;
}

@media (max-width: 768px) {
  .c-body {
    padding-top: 70px;
  }

  /* CoreUI adds generous top padding to main content by default. The fixed
     header is already accounted for by .c-body, so only a small visual gap is
     needed before the first card. */
  .c-main {
    padding-top: 10px;
  }
}

/* Shared tablet and desktop content spacing. */
@media (min-width: 768px) {
  .c-main {
    width: 100%;
    padding: 18px 0 0;
  }

  .c-main > .container-fluid {
    width: 100%;
    max-width: none;
    padding-right: 18px;
    padding-left: 18px;
  }
}

/* The fixed sidebar is used only on wide desktop screens. */
@media (min-width: 1201px) {
  .sidebar-open {
    margin-left: 250px !important;
  }
  .sidebar-minimized {
    margin-left: 70px !important;
  }
}

@media (max-width: 1200px) {
  .c-wrapper {
    margin-left: 0 !important;
  }
  .c-body {
    padding-bottom: 75px !important; /* Space for Bottom Navigation Bar */
  }
}
</style>

<style>
/* Structural bars stay straight; surfaces and controls use intentional radii. */
body.user-unified-rounded-ui .app-header-custom,
body.user-unified-rounded-ui .reg-sidebar,
body.user-unified-rounded-ui .u-bottom-nav {
  border-radius: 0 !important;
  box-shadow: none !important;
}

body.user-unified-rounded-ui .user-page-banner,
body.user-unified-rounded-ui .manual-header,
body.user-unified-rounded-ui .detail-header {
  border-radius: 12px !important;
  box-shadow: none !important;
}

body.user-unified-rounded-ui .card:not(.user-page-banner),
body.user-unified-rounded-ui .send-report-page,
body.user-unified-rounded-ui .wizard-progress-block,
body.user-unified-rounded-ui .page-form > div:not(.page-actions),
body.user-unified-rounded-ui .flowchart-container,
body.user-unified-rounded-ui .flow-step,
body.user-unified-rounded-ui .job-card,
body.user-unified-rounded-ui .area-card,
body.user-unified-rounded-ui .detail-box,
body.user-unified-rounded-ui .image-box,
body.user-unified-rounded-ui .evaluation-box {
  border-radius: 12px !important;
}

body.user-unified-rounded-ui .card.manual-card {
  border-radius: 0 !important;
}

body.user-unified-rounded-ui .card.detail-card {
  border-radius: 0 0 12px 12px !important;
}

body.user-unified-rounded-ui button,
body.user-unified-rounded-ui .btn,
body.user-unified-rounded-ui .form-control,
body.user-unified-rounded-ui .custom-select,
body.user-unified-rounded-ui .dropdown-menu,
body.user-unified-rounded-ui .modal-content,
body.user-unified-rounded-ui .swal2-popup {
  border-radius: 8px !important;
}

body.user-unified-rounded-ui .btn,
body.user-unified-rounded-ui .card-btn,
body.user-unified-rounded-ui .history-detail-btn,
body.user-unified-rounded-ui .history-mobile-foot button {
  min-height: 40px;
  padding: 8px 16px;
  box-shadow: none !important;
  transform: none !important;
  font-weight: 700;
}

body.user-unified-rounded-ui .btn-sm {
  min-height: 34px;
  padding: 6px 12px;
}

body.user-unified-rounded-ui .btn:hover,
body.user-unified-rounded-ui .card-btn:hover,
body.user-unified-rounded-ui .history-detail-btn:hover,
body.user-unified-rounded-ui .history-mobile-foot button:hover {
  box-shadow: none !important;
  transform: none !important;
}

body.user-unified-rounded-ui .icon-circle,
body.user-unified-rounded-ui .history-icon,
body.user-unified-rounded-ui .flow-icon,
body.user-unified-rounded-ui .step-circle,
body.user-unified-rounded-ui .check,
body.user-unified-rounded-ui .rounded-circle {
  border-radius: 50% !important;
}

body.user-unified-rounded-ui .pill-btn {
  border-radius: 999px !important;
}

body.user-unified-rounded-ui .circle-success-btn,
body.user-unified-rounded-ui .circle-action-btn,
body.user-unified-rounded-ui .circle-cancel-btn {
  border-radius: 50% !important;
}

/* Keep action colors predictable: maroon actions never turn Bootstrap green. */
body.user-unified-rounded-ui .btn-success,
body.user-unified-rounded-ui .btn-primary,
body.user-unified-rounded-ui .wizard-next-btn,
body.user-unified-rounded-ui .evaluation-submit-btn,
body.user-unified-rounded-ui .swal2-confirm {
  color: #fff !important;
  background: #1e40af !important;
  border-color: #1e40af !important;
}

body.user-unified-rounded-ui .btn-success:hover,
body.user-unified-rounded-ui .btn-success:focus,
body.user-unified-rounded-ui .btn-primary:hover,
body.user-unified-rounded-ui .btn-primary:focus,
body.user-unified-rounded-ui .wizard-next-btn:hover,
body.user-unified-rounded-ui .wizard-next-btn:focus,
body.user-unified-rounded-ui .evaluation-submit-btn:hover,
body.user-unified-rounded-ui .evaluation-submit-btn:focus,
body.user-unified-rounded-ui .swal2-confirm:hover {
  color: #fff !important;
  background: #1e3a8a !important;
  border-color: #1e3a8a !important;
}

body.user-unified-rounded-ui .btn-outline-danger {
  color: #1e40af !important;
  background: #fff !important;
  border-color: #1e40af !important;
}

body.user-unified-rounded-ui .btn-outline-danger:hover,
body.user-unified-rounded-ui .btn-outline-danger:focus {
  color: #1e40af !important;
  background: #f0f9ff !important;
  border-color: #1e40af !important;
}

body.user-unified-rounded-ui .hero-action:hover,
body.user-unified-rounded-ui .hero-action:focus {
  color: #1e40af !important;
  background: #f0f9ff !important;
  border-color: #fff !important;
}

/* Use the MFU accent for keyboard/touch focus instead of Bootstrap blue. */
body.user-unified-rounded-ui button:focus,
body.user-unified-rounded-ui button:focus-visible,
body.user-unified-rounded-ui .btn:focus,
body.user-unified-rounded-ui .btn.focus,
body.user-unified-rounded-ui .btn:active:focus,
body.user-unified-rounded-ui .form-control:focus,
body.user-unified-rounded-ui .custom-select:focus,
body.user-unified-rounded-ui select:focus,
body.user-unified-rounded-ui textarea:focus,
body.user-unified-rounded-ui input:focus {
  outline: none !important;
  border-color: #1e40af !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12) !important;
}

body.user-unified-rounded-ui a:focus,
body.user-unified-rounded-ui a:focus-visible {
  outline: none !important;
  box-shadow: none !important;
}

body.user-unified-rounded-ui .u-bottom-nav .nav-item:focus,
body.user-unified-rounded-ui .u-bottom-nav .nav-item:focus-visible,
body.user-unified-rounded-ui .u-bottom-nav .nav-item:active {
  outline: none !important;
  border-color: transparent !important;
  box-shadow: none !important;
}

body.user-unified-rounded-ui .app-header-custom .header-menu-toggle:focus,
body.user-unified-rounded-ui .app-header-custom .header-menu-toggle:focus-visible,
body.user-unified-rounded-ui .app-header-custom .header-menu-toggle:active {
  outline: none !important;
  border-color: transparent !important;
  box-shadow: none !important;
}

body.user-unified-rounded-ui .reg-sidebar .menu-link,
body.user-unified-rounded-ui .reg-sidebar .menu-link:hover,
body.user-unified-rounded-ui .reg-sidebar .menu-link:focus,
body.user-unified-rounded-ui .reg-sidebar .menu-link:focus-visible,
body.user-unified-rounded-ui .reg-sidebar .menu-link:active,
body.user-unified-rounded-ui .reg-sidebar .menu-link.active {
  border: 0 !important;
  outline: none !important;
  box-shadow: none !important;
  -webkit-tap-highlight-color: transparent;
}

body.user-unified-rounded-ui .wizard-step-one .wizard-next-btn {
  flex: 0 1 360px !important;
  margin-left: auto !important;
}

/* A single SweetAlert visual language for every User popup. */
body.user-unified-rounded-ui .swal2-popup {
  width: min(460px, calc(100vw - 28px)) !important;
  padding: 0 !important;
  overflow: hidden;
  color: #3d3030;
  background: #fff !important;
  border: 1px solid rgba(37, 99, 235, 0.13) !important;
  box-shadow: none !important;
  border-radius: 12px !important;
}

body.user-unified-rounded-ui .swal2-title {
  margin: 0 !important;
  padding: 22px 24px 17px !important;
  color: #1e40af !important;
  background: #f8fafc;
  border-bottom: 1px solid rgba(37, 99, 235, 0.1);
  font-size: 1.45rem !important;
  font-weight: 800 !important;
  text-align: left !important;
}

body.user-unified-rounded-ui .swal2-html-container,
body.user-unified-rounded-ui .swal2-content {
  margin: 0 !important;
  padding: 18px 24px 8px !important;
  color: #4b4040 !important;
}

body.user-unified-rounded-ui .swal2-icon {
  width: 64px !important;
  height: 64px !important;
  margin: 20px auto 10px !important;
  transform: none;
  font-size: 12px !important;
  border-width: 3px !important;
}

body.user-unified-rounded-ui .swal2-icon .swal2-icon-content {
  font-size: 42px !important;
}

body.user-unified-rounded-ui .swal2-popup:has(.swal2-icon) .swal2-title {
  padding-top: 16px !important;
  text-align: center !important;
}

body.user-unified-rounded-ui .swal2-popup:has(.swal2-icon) .swal2-html-container,
body.user-unified-rounded-ui .swal2-popup:has(.swal2-icon) .swal2-content {
  text-align: center !important;
}

body.user-unified-rounded-ui .swal2-textarea,
body.user-unified-rounded-ui .swal2-input {
  width: calc(100% - 48px) !important;
  margin: 12px 24px 4px !important;
  border: 1px solid #d8b8b8 !important;
  box-shadow: none !important;
}

body.user-unified-rounded-ui .swal2-actions:not(.repair-confirm-actions) {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.25fr);
  gap: 12px;
  width: 100%;
  margin: 12px 0 0 !important;
  padding: 16px 24px 20px !important;
  background: #f8fafc;
  border-top: 1px solid rgba(37, 99, 235, 0.08);
}

body.user-unified-rounded-ui .swal2-actions:not(.repair-confirm-actions) .swal2-cancel {
  grid-column: 1;
  color: #1e40af !important;
  background: #fff !important;
  border: 1px solid #1e40af !important;
}

body.user-unified-rounded-ui .swal2-actions:not(.repair-confirm-actions) .swal2-confirm {
  grid-column: 2;
  color: #fff !important;
  background: #1e40af !important;
  border: 1px solid #1e40af !important;
}

/* Informational/result popups have one action, so keep it visually centred. */
body.user-unified-rounded-ui .swal2-actions:not(.repair-confirm-actions) .swal2-confirm:only-child,
body.user-unified-rounded-ui .swal2-actions:not(.repair-confirm-actions) .swal2-cancel:only-child {
  grid-column: 1 / -1 !important;
  justify-self: center;
  width: min(220px, 100%) !important;
}

body.user-unified-rounded-ui .swal2-actions button {
  width: 100%;
  min-height: 44px;
  margin: 0 !important;
  box-shadow: none !important;
}

body.user-unified-rounded-ui .swal2-actions button:hover {
  transform: none !important;
  box-shadow: none !important;
}

@media (max-width: 520px) {
  body.user-unified-rounded-ui .swal2-title {
    padding: 18px 18px 14px !important;
    font-size: 1.25rem !important;
  }

  body.user-unified-rounded-ui .swal2-html-container,
  body.user-unified-rounded-ui .swal2-content {
    padding: 16px 18px 6px !important;
    font-size: 1rem !important;
    line-height: 1.5 !important;
  }

  body.user-unified-rounded-ui .swal2-actions {
    gap: 10px;
    padding: 14px 18px 18px !important;
  }
}

/* User notifications use the same clean popup surface as every other role. */
body.user-unified-rounded-ui .swal2-container .swal2-popup:not(.repair-confirm-popup) {
  width: min(460px, calc(100vw - 28px)) !important;
  padding: 24px !important;
  overflow: visible !important;
  background: #fff !important;
  border: 1px solid rgba(37, 99, 235, 0.14) !important;
  border-radius: 16px !important;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24) !important;
}

body.user-unified-rounded-ui .swal2-container .swal2-title:not(.repair-confirm-title) {
  margin: 8px 0 0 !important;
  padding: 0 !important;
  color: #1e40af !important;
  background: transparent !important;
  border: 0 !important;
  font-size: 1.35rem !important;
  font-weight: 700 !important;
  text-align: center !important;
}

body.user-unified-rounded-ui .swal2-container .swal2-html-container:not(.repair-confirm-content),
body.user-unified-rounded-ui .swal2-container .swal2-content:not(.repair-confirm-content) {
  margin: 1em 1.6em 0.3em !important;
  padding: 0 !important;
  text-align: center !important;
}

body.user-unified-rounded-ui .swal2-container .swal2-icon {
  width: 5em !important;
  height: 5em !important;
  margin: 8px auto 1.5em !important;
}

body.user-unified-rounded-ui .swal2-container .swal2-actions:not(.repair-confirm-actions) {
  display: flex !important;
  width: auto !important;
  margin: 1.25em auto 0 !important;
  padding: 0 !important;
  background: transparent !important;
  border: 0 !important;
}

body.user-unified-rounded-ui .user-page-banner {
  display: flex !important;
  align-items: center !important;
  width: 100%;
  color: #fff !important;
  background: #1e40af !important;
  border: 0 !important;
  box-shadow: none !important;
  border-radius: 12px !important;
}

body.user-unified-rounded-ui .app-header-custom,
body.user-unified-rounded-ui .reg-sidebar,
body.user-unified-rounded-ui .u-bottom-nav {
  box-shadow: none !important;
}

body.user-unified-rounded-ui .user-page-banner h2,
body.user-unified-rounded-ui .user-page-banner .page-title,
body.user-unified-rounded-ui .user-page-banner .card-title {
  margin: 0 !important;
  color: #fff !important;
  font-size: 1.9rem !important;
  font-weight: 800 !important;
  line-height: 1.25 !important;
}

@media (min-width: 769px) {
  body.user-unified-rounded-ui .user-page-banner {
    height: 112px;
    min-height: 112px;
    margin-top: 0 !important;
    padding: 22px 28px !important;
  }

  body.user-unified-rounded-ui .user-page-banner .hero-card-body {
    width: 100%;
    height: 100%;
    padding: 0 !important;
  }

  body.user-unified-rounded-ui .user-page-banner .page-title,
  body.user-unified-rounded-ui .user-page-banner .page-subtitle {
    color: #fff !important;
  }

  body.user-unified-rounded-ui .user-page-banner .page-close-btn {
    color: #fff !important;
  }

  body.user-unified-rounded-ui .user-page-banner .page-subtitle {
    margin-bottom: 0;
    color: rgba(255, 255, 255, 0.78) !important;
  }

  body.user-unified-rounded-ui .manual-header.user-page-banner {
    margin-bottom: 0 !important;
  }
}

@media (max-width: 768px) {
  body.user-unified-rounded-ui .user-page-banner {
    height: 88px;
    min-height: 88px;
    margin-top: 0 !important;
    margin-bottom: 16px !important;
    padding: 14px 16px !important;
  }

  body.user-unified-rounded-ui .user-page-banner .hero-card-body {
    width: 100%;
    height: 100%;
    flex-direction: row !important;
    align-items: center !important;
    padding: 0 !important;
  }

  body.user-unified-rounded-ui .user-page-banner .hero-action {
    width: auto;
    flex: 0 0 auto;
    margin-left: auto;
    padding: 9px 12px;
    white-space: nowrap;
  }

  body.user-unified-rounded-ui .user-page-banner .page-title,
  body.user-unified-rounded-ui .user-page-banner .page-subtitle,
  body.user-unified-rounded-ui .user-page-banner .page-close-btn {
    color: #fff !important;
  }

  body.user-unified-rounded-ui .manual-header.user-page-banner {
    margin-bottom: 0 !important;
  }

  body.user-unified-rounded-ui .user-page-banner h2,
  body.user-unified-rounded-ui .user-page-banner .page-title,
  body.user-unified-rounded-ui .user-page-banner .card-title {
    font-size: 1.4rem !important;
  }
}

@media (max-width: 520px) {
  body.user-unified-rounded-ui .user-page-banner .hero-action-label-full {
    display: inline;
  }

  body.user-unified-rounded-ui .user-page-banner .hero-action-label-compact {
    display: none;
  }

  body.user-unified-rounded-ui .user-page-banner .hero-action {
    padding-right: 10px;
    padding-left: 10px;
  }

  body.user-unified-rounded-ui .user-page-banner h2,
  body.user-unified-rounded-ui .user-page-banner .page-title,
  body.user-unified-rounded-ui .user-page-banner .card-title {
    font-size: 1.22rem !important;
  }
}
</style>
